import { mean, variance, median, sd, iqr } from 'mdatools/stat';
import { ismatrix, Vector } from 'mdatools/arrays';

import { qchisq } from 'mdatools/distributions';
import { scale as prep_scale} from 'mdatools/prep';

import { genhash, seq, replacer as baseReplacer, reviver as baseReviver, convertToIntervals,
   checkedResultOutliers, checkedRowNumbers } from '@mdatools-apps/shared/utils';
import { processMembers, processStrangers } from '@mdatools-apps/shared/methods/simca.js';


export function replacer(key, value) {
   if (value instanceof NIMCA) return value.asJSON();
   return baseReplacer(key, value);
}

export function reviver(key, value) {
   if (value && typeof value === "object" && value.__type) {
      if (value.__type === 'NIMCA') return NIMCA.fromJSON(value);
      if (value.__type === 'NIMCARes') return null;
   }
   return baseReviver(key, value);
}


/**
 * Download CSV file with NIMCA results.
 *
 * @param {Object} nimca - JSON with NIMCA model.
 * @param {Object} res - JSON with results object.
 * @param {Object} dataset - JSON with original dataset.
 * @param {string} name - name of the results ('cal', 'test', 'pv', 'new')
 */
export function downloadResults(nimca, res, dataset, name) {

   // the result records the objects it was computed without, so the export can not be sliced with
   // a list of excluded objects which belongs to some other dataset
   // an entry that names no real row is silent: it matches no whole 'i' below, so a row the
   // result has no values for is emitted and the file is written misaligned. see
   // 'checkedResultOutliers' for what is refused and why none of it is caught further down
   const outliers = checkedResultOutliers(res.outliers, dataset.values.nrows);

   let exclvars = nimca.exclvars;
   if (!exclvars || !Array.isArray(exclvars)) {
      exclvars = [];
   }

   const nAll = dataset.values.nrows;
   const nOut = outliers.length;
   const n = res.nrows;

   if (nAll - nOut !== n) {
      throw new Error(
         'Results do not match the dataset: ' + nAll + ' rows minus ' + nOut +
         ' excluded is not ' + n + '.'
      );
   }

   const o = res.outcomes;
   const sep = dataset.sep;
   const dec = dataset.dec;

   // every number is written with the file's decimal separator as the row is built. the old code
   // ran one replace over the finished document instead, which rewrote every dot in it: an object
   // label 'sample.1' was exported as 'sample,1', while the title, the target class and the file
   // name were added after the replace and kept theirs, so one name could be spelled two ways in
   // the same file. only a value which is a number, or a string which is nothing but a number,
   // is converted here - text keeps whatever it contains.
   //
   // a stranger distribution which could not be fitted leaves 'beta', 's' and 'f0' at NaN, and
   // 'selectivity' is 1 - beta so it goes with them. the javascript spelling of a non-finite
   // number is not something a spreadsheet reads as a value, so they are written as text
   const num2str = v => {
      if (typeof v === 'number') {
         if (Number.isNaN(v)) return '-';
         if (v === Infinity) return 'Inf';
         if (v === -Infinity) return '-Inf';
         return String(v).replace('.', dec);
      }
      return typeof v === 'string' && /^-?\d+\.\d+$/.test(v) ? v.replace('.', dec) : v;
   };

   const row = (values, end = '\n') => values.map(num2str).join(sep) + end;

   const hasClasses = res.classes && res.classes.length === n;
   // the metadata rows are padded out to the width of the object table below them, which is six
   // columns wide whether or not the set carries classes
   const filler = ['', '', '', ''];
   let out = '';
   out += row(['Reference classes:',  hasClasses ? 'provided' : 'not provided', ...filler]);
   out += row(['Center:', (nimca.center ? 'yes' : 'no'), ...filler]);
   out += row(['Scale:', (nimca.scale ? 'yes' : 'no'), ...filler]);
   out += row(['Alpha:', (nimca.alpha/100).toFixed(3), ...filler]);
   out += row(['Limits estimator:', nimca.limType, ...filler]);
   out += '\n';

   out += row(['Nf:', o.Nf, ...filler]);
   out += '\n';

   out += row(['F crit (extremes):', nimca.eCrit, ...filler]);
   out += row(['F crit (outliers):', nimca.oCrit, ...filler]);
   out += '\n';

   if (exclvars.length > 0) {
      out += row(['Number of excluded variables:', exclvars.length, ...filler]);
      // the model's list is 0-based; the file is read by people, who number variables from 1
      out += row(['Indices of excluded variables:', convertToIntervals(exclvars.map(i => i + 1)),
         ...filler]);
      out += '\n';
   }

   // overall statistics
   out += row(['Objects:', nAll, ...filler]);
   out += row(['In:', o.decsNum.in, ...filler]);
   out += row(['Out:', o.decsNum.out, ...filler]);
   out += row(['Excluded:', nOut, ...filler]);

   // statistics for members if any
   if (res.nMembers > 0) {
      out += '\n';
      out += row(['Members:', res.nMembers, ...filler]);
      out += row(['Regular:', o.rolsNum.regular, ...filler]);
      out += row(['Extreme:', o.rolsNum.extreme, ...filler]);
      out += row(['Outliers:', o.rolsNum.outlier, ...filler]);
      out += row(['Sensitivity:', o.stat.sensitivity, ...filler]);
   }

   // statistics for strangers if any
   if (res.nStrangers > 0) {
      out += '\n';
      out += row(['Strangers:', res.nStrangers, ...filler]);
      out += row(['In:', o.decsNum.in - o.rolsNum.regular, ...filler]);
      out += row(['Aliens:', o.rolsNum.alien, ...filler]);
      out += row(['External:', o.rolsNum.external, ...filler]);
      out += row(['Specificity:', o.stat.specificity, ...filler]);
      out += row(['Selectivity:', o.stat.selectivity, ...filler]);

      out += '\n';
      out += row(['Beta:', o.beta, ...filler]);
      out += row(['Non-centrality (s):', o.s, ...filler]);
      out += row(['Scaling (f0):', o.f0, ...filler]);

      if (res.nMembers > 0) {
         const TPR = o.stat.sensitivity;
         const FPR = 1 - o.stat.specificity;

         // a ratio with a zero denominator is either undefined (nothing over nothing) or infinite,
         // and writing the javascript spelling of either into the file gives a column which no
         // spreadsheet reads as a number
         const ratio = (num, den) => den !== 0 ? num / den : (num === 0 ? '-' : 'Inf');

         const PPV = ratio(o.TP, o.TP + o.FP);

         // f1 is taken from the counts rather than from precision and recall. the usual form goes
         // undefined as soon as precision is zero over zero, while the count form is defined and
         // equal to zero whenever any member or stranger was classified at all
         const F1 = ratio(2 * o.TP, 2 * o.TP + o.FP + o.FN);
         const PLR = ratio(TPR, FPR);
         const NLR = ratio(1 - TPR, 1 - FPR);
         out += '\n';
         out += row(['Efficiency:', o.stat.efficiency, ...filler]);
         out += row(['Accuracy:', o.stat.accuracy, ...filler]);
         out += row(['Precision:', PPV, ...filler]);
         out += row(['F1-score:', F1, ...filler]);
         out += row(['PLR:', PLR, ...filler]);
         out += row(['NLR:', NLR, ...filler]);
      }
   }


   // outcomes for every object
   out += '\n';
   out += row(['', 'object', 'class', 'role', 'decision', 'f']);


   let j = 0;
   for (let i = 0; i < nAll; i++) {

      if (nOut > 0 && outliers.includes(i)) {
         out += row([
            '',
            dataset.objlabels[i],
            '-',
            'removed',
            '-',
            '-'
         ]);
         continue;
      }

      out += row([
         '',
         dataset.objlabels[i],
         // a set loaded without a class column has no classes at all, and reading one anyway put
         // the text 'undefined' in every row of the file while the header said 'not provided'.
         // the column stays so that the rows keep the width of the header above them
         hasClasses ? res.classes[j] : '',
         o.rols[j],
         o.decs[j],
         o.f[j]
      ]);
      j = j + 1;
   }
   out += '\n';


   out = row(['Target class:', nimca.className, ...filler]) + out;
   out = row(['Data filename:', dataset.fileName, ...filler]) + out;

   let hdr = row(['NIMCA results (' + name + ')', 'https://mda.tools/nimca/', ...filler], '\n\n');

   const blob = new Blob([hdr + out], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `nimca-results-${name}.csv`);
   a.click()
   URL.revokeObjectURL(url)
}


/**
 * Download DDSIMCA model as JSON file.
 *
 * @param {Object} nimca - JSON with NIMCA model.
 * @param {Object} prepmodel - JSON with preprocessing model.
 */
export function downloadModel(nimca, prep) {

   const out = {
      'nimca': nimca.asJSON(),
      'prep': prep
   }
   const blob = new Blob([JSON.stringify(out, replacer)], { type: 'application/json' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', 'nimca-model.json');
   a.click()
   URL.revokeObjectURL(url)
}


/**
 * Class for Naive SIMCA results
 */
export class NIMCARes {

   /**
    * Constructor
    * @param {Vector} d - vector with distance values
    * @param {} ind
    * @param {*} objlabels
    * @param {*} objindices
    * @param {*} classes
    * @param {*} classNames
    * @param {*} m
    * @param {Array} outliers - objects excluded from the set this result was computed on.
    */
   constructor(d, ind, objlabels, objindices, classes, classNames, m, outliers = []) {
      this.d = d;
      this.ind = ind;
      // the exporter reads the excluded objects from here rather than being handed a list which
      // may belong to a different dataset and would slice the export with the wrong stride
      this.outliers = Array.isArray(outliers) ? [...outliers] : [];
      this.objlabels = objlabels;
      this.objindices = objindices;
      this.classes = classes;
      this.classNames = classNames;
      this.nrows = d.length;
      this.nMembers = ind.members.length;
      this.nStrangers = ind.strangers.length;
      this.nUnknown = ind.unknown.length;
      this.outcomes = $derived(m.oCrit && m.eCrit ? this.getOutcomes(m) : null);
   }

   getOutcomes(model) {

      const f = this.d.apply(v => v / model.f0 * model.Nf).v;
      const decs = new Array(this.nrows);
      const rols = new Array(this.nrows);
      const rolsNum = {'regular': 0, 'extreme': 0, 'outlier': 0, 'alien': 0, 'external': 0};
      const decsNum = {'in': 0, 'out': 0};

      const [TP, FN] = processMembers(f, model.eCrit, model.oCrit, rols, decs, rolsNum, decsNum, this.ind.members);
      const [TN, FP, beta, s, f0, hz, Mz, Sz, k, m] = processStrangers(f, model.Nf, model.eCrit, model.oCrit, rols, decs, rolsNum, decsNum, this.ind.strangers);

      if (this.nUnknown > 0) {
         // the return value is not used here - the call is made for its side effects, it fills
         // 'rols', 'decs', 'rolsNum' and 'decsNum' for the objects of unknown class membership,
         // which is the whole classification shown in the Predict tab
         processStrangers(f, model.Nf, model.eCrit, model.oCrit, rols, decs, rolsNum, decsNum, this.ind.unknown);
      }

      const sensitivity = TP > 0 ? TP / (TP + FN) : 0;
      const specificity = TN > 0 ? TN / (TN + FP) : 0;
      const selectivity = 1 - beta;
      const efficiency = Math.sqrt(sensitivity * specificity);
      // the guard belongs on the denominator: as a guard on TP and TN it reported 0 for a model
      // which classified almost everything right but accepted no member, or no stranger
      const nTotal = TP + TN + FP + FN;
      const accuracy = nTotal > 0 ? (TP + TN) / nTotal : 0;

      const stat = {sensitivity, specificity, selectivity, efficiency, accuracy};
      return {Nf: model.Nf, f, decs, rols, decsNum, rolsNum, TP, FN, TN, FP, beta, s, f0, hz, Mz, Sz, k, m, stat}
   }

   // results are computed from the model and the data, so they are kept out of the session.
   // 'JSON.stringify' calls this before the replacer sees the object, so what is written is a
   // plain null and there is no way back from it - on purpose
   toJSON() {
      return null;
   }
}


/**
 * Class for NIMCA model
 */
export class NIMCA {

   /**
    * Class constructor
    * @param {string} className - name of the target class.
    * @param {Vector} mX - vector with values for centering.
    * @param {Vector} sX - vector with values for scaling.
    * @param {number} nrows - number of rows in the training set.
    * @param {number} ncols - number of columns (predictors).
    * @param {number} Nfc - number of degrees of freedom computed using classic approach.
    * @param {number} Nfr - number of degrees of freedom computed using robust approach.
    * @param {number} f0c - scalar computed using classic approach.
    * @param {number} f0r - scalar computed using robust approach.
    * @param {string} hash - unique ID (hash).
    * @param {number} alpha - (state) significance level for classification.
    * @param {number} gamma - (state) significance level for outliers detection.
    * @param {string} limType - (state) limit type for current model.
    * @param {string} calsetToken - (state) id of the calibration set the model was fitted on.
    */
   constructor(className, mX, sX, nrows, ncols, Nfc, Nfr, f0c, f0r, hash, alpha, gamma, limType, center, scale, fileName = 'nimca-model', calsetToken = null) {

      // constant properties
      this.mX = mX;
      this.sX = sX;
      this.nrows = nrows;
      this.ncols = ncols;
      this.className = className;
      this.Nfc = Nfc;
      this.Nfr = Nfr;
      this.f0c = f0c;
      this.f0r = f0r;
      this.hash = hash;
      this.class = ['nimcamodel'];
      this.fileName = fileName;
      this.center = center;
      this.scale = scale;

      // state properties
      this.alpha = $state(alpha);
      this.gamma = $state(gamma);
      this.limType = $state(limType);

      // which calibration set this model was fitted on. '$state' because the session holds NIMCA
      // instances and svelte does not proxy those, so a plain field would change without anything
      // noticing - not the backup effect, and not the guard in 'NIMCASettings'
      this.calsetToken = $state(calsetToken);

      // derived properties
      this.Nf = $derived(this.limType === 'classic' ? this.Nfc : this.Nfr);
      this.f0 = $derived(this.limType === 'classic' ? this.f0c : this.f0r);
      this.eConfLim = $derived(1 - Number.parseFloat(this.alpha) / 100);
      this.oConfLim = $derived(Math.pow(1 - this.gamma / 100, 1 / nrows));
      this.eCrit = $derived(qchisq(this.eConfLim, this.Nf));
      this.oCrit = $derived(qchisq(this.oConfLim, this.Nf));
   }

   /**
    * Applies NIMCA model to new dataset.
    * @param {Object} dataset - JSON with dataset.
    * @param {Array} outliers - array with outliers.
    * @returns {NIMCARes} result object.
    */
   predict(dataset, outliers = []) {

      if (!dataset.values || !ismatrix(dataset.values)) {
         throw new Error("nimca.predict: 'dataset' does not contain values as Matrix object.");
      }

      if (dataset.values.ncols != this.ncols) {
         throw new Error("nimca.predict: 'dataset' values have wrong number of columns (expect: " + this.ncols + ").");
      }

      // this is one of the two routes which reach a result without passing 'handleOutliers', and
      // it is the one the app takes most of the time: all three tabs assign the RAW dataset to
      // their '*seto' when no outlier is marked, and only the non-empty branch of
      // 'excludeOutliers' meets that guard. the preprocessing between there and here does not
      // check the list either - 'PrepWidget' builds 'calsetp', tab-local 'prepApply' functions
      // build 'testsetp' and 'newsetp', and both only trim columns. checking here closes all three
      // tabs at once. the list itself is passed on rather than a copy, since 'checkedRowNumbers'
      // gives back the array it was given
      const rowNumbers = checkedRowNumbers(dataset.objindices, dataset.values.nrows);

      const d = NIMCA.getDistance(dataset.values, this.mX, this.sX);
      const ind = {members: [], strangers: [], unknown: []};
      if (dataset.classes && dataset.classes.length === dataset.values.nrows) {
         for (let i = 0; i < dataset.classes.length; i++) {
            if (dataset.classes[i] === this.className) {
               ind.members.push(i);
            } else {
               ind.strangers.push(i);
            }
         }
      } else {
         ind.unknown = seq(0, dataset.values.nrows - 1);
      }
      return new NIMCARes(d, ind, dataset.objlabels, rowNumbers, dataset.classes, dataset.classNames, this, outliers);
   }


   /**
    * Converts model object to pure JSON.
    * @returns {Object}
    */
   asJSON() {
      return {
         __type: 'NIMCA',
         class: this.class,
         fileName: this.fileName,
         mX: this.mX,
         sX: this.sX,
         nrows: this.nrows,
         ncols: this.ncols,
         className: this.className,
         Nfc: this.Nfc,
         Nfr: this.Nfr,
         f0c: this.f0c,
         f0r: this.f0r,
         hash: this.hash,
         center: this.center,
         scale: this.scale,
         alpha: this.alpha,
         gamma: this.gamma,
         limType: this.limType,
         calsetToken: this.calsetToken
      };
   }

   /**
    * Creates a model based on the training set.
    * @param {Object} calset - training set.
    * @param {Array} outliers - outliers.
    * @param {Object} params - initial state parameters for the model.
    * @returns {NIMCA} object with model.
    */
   static fit(calset, center, scale, params = {alpha: 5, gamma: 1, limType: 'classic'}) {

      if (!calset.values) {
         throw new Error("nimca.fit: parameter 'calset' does not contain values as Matrix object.");
      }

      if (!calset.classNames || calset.classNames.length != 1) {
         throw new Error("nimca.fit: parameter 'calset' should contain class labels for a target class only.");
      }

      let X = calset.values;
      const className = calset.classNames[0];

      if (!ismatrix(X)) {
         throw new Error("nimca.fit: X must be a matrix.");
      }

      // here X is already without outliers, so we do not provide them to getDistance() method
      const mX = center ? X.apply(mean, 2) : Vector.zeros(X.ncols);
      const sX = scale ? X.apply(sd, 2) : Vector.ones(X.ncols);
      const d = NIMCA.getDistance(X, mX, sX);

      // classic estimates
      const f0c = mean(d);
      const s2f = variance(d);
      let Nfc = Math.round(2 * f0c * f0c / s2f);
      if (Nfc < 1) Nfc = 1;
      if (Nfc > 250) Nfc = 250;


   // Mu <- apply(U, 2, median)
   // Su <- apply(U, 2, IQR)

   // RM <- Su / Mu
   // Nu <- round(exp((1.380948 * log(2.68631 / RM)) ^ 1.185785))
   // Nu[RM > 2.685592117] <- 1
   // Nu[RM < 0.194565995] <- 100

   // u0 <- 0.5 * Nu * (Mu / qchisq(0.50, Nu) + Su / (qchisq(0.75, Nu) - qchisq(0.25, Nu)))
   // return(list(u0 = u0, Nu = Nu, nobj = nrow(U)))

      const Mf = median(d);
      const Sf = iqr(d);
      const RM = Sf / Mf;

      let Nfr;
      if (RM > 2.685592117) {
         Nfr = 1;
      } else if (RM < 0.194565995) {
         Nfr = 100;
      } else {
         Nfr = Math.round(Math.exp(Math.pow(1.380948 * Math.log(2.68631 / RM), 1.185785)));
      }

      // robust estimate for f0
      const f0r = 0.5 * Nfr * (Mf / qchisq(0.50, Nfr) + Sf / (qchisq(0.75, Nfr) - qchisq(0.25, Nfr)));

      // a training set with no spread in its distances - two identical rows will do it - makes
      // 'variance(d)' zero, so 'Nfc' is 'Math.round(0/0)', which is NaN. neither clamp above
      // rejects NaN, 'qchisq' does not return NaN when given it, and the model that came out of
      // this looked ordinary while classifying everything wrongly
      if (!Number.isInteger(Nfc) || !Number.isInteger(Nfr) ||
          !Number.isFinite(f0c) || !Number.isFinite(f0r) ||
          Nfc < 1 || Nfr < 1 || f0c <= 0 || f0r <= 0) {
         throw new Error('The training set has no usable variation in the distances - check its size and values.');
      }

      return new NIMCA(className, mX, sX, X.nrows, X.ncols, Nfc, Nfr, f0c, f0r, genhash(),
         params.alpha, params.gamma, params.limType, center, scale);
   }


   /**
    * Creates NIMCA object from pure JSON representation.
    * @param {Object} obj - JSON with model parameters.
    * @returns {NIMCA} object with model.
    */
   static fromJSON(obj) {
      const {__type, mX, sX, nrows, ncols, className, Nfc, Nfr, f0c, f0r, hash, alpha, gamma, limType, fileName, center, scale, calsetToken} = obj;
      return new NIMCA(className, mX, sX, nrows, ncols, Nfc, Nfr, f0c, f0r, hash, alpha, gamma, limType, center, scale, fileName, calsetToken ?? null);
   }

   /**
    * Computes full distance for new data values.
    * @param {Matrix} X - matrix with data values.
    * @param {Vector} mX - vector with values for centering.
    * @param {Vector} sX - vector with values for scaling.
    * @returns {Vector} vector with distances.
    */
   static getDistance(X, mX, sX) {
      const Xp = prep_scale(X, mX, sX);
      const nrows = Xp.nrows;
      const ncols = Xp.ncols;
      const d = Vector.zeros(nrows);
      for (let c = 0; c < ncols; c++) {
         const xc = Xp.getcolref(c + 1);
         for (let r = 0; r < nrows; r++) {
            d.v[r] += xc[r] ** 2;
         }
      }
      return d;
   }
}
